import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import type { RootState } from "../../../store/store";
import { LOGINPAGE } from "../../auth/routes";
import EcoIcon, { EcoIconName } from "../../../components/eco-icon/EcoIcon";
import HomeNoticeBanner from "../../../components/notices/HomeNoticeBanner";
import { useStaffProfile } from "../../../hooks/useStaffProfile";

import SelectFreelancersForEvaluationModal, {
  EvaluationFreelancerCandidate,
} from "../../../components/modal/evaluation/SelectFreelancersForEvaluationModal";
import SelectStoresForEvaluationModal from "../../../components/modal/evaluation/SelectStoresForEvaluationModal";
import ViewSubmittedEvaluationsModal from "../../../components/modal/evaluation/ViewSubmittedEvaluationsModal";
import ViewStoreEvaluationsModal from "../../../components/modal/evaluation/ViewStoreEvaluationsModal";

import { getMyStoresPaginatedApi } from "../../../views/store/storeApi";
import {
  listMyOpenStoreEvaluationsApi,
  listStoreEvaluationsApi,
  MyOpenStoreEvaluation,
} from "../../../views/store-evaluations/storeEvaluationsApi";
import { getMyCoverageApi, MyCoverage } from "../../../views/store-evaluations/coverageApi";

import {
  getFreelancersBySlotApi,
  PaginatedSlotFreelancersResponse,
  SlotFreelancerItem,
} from "../../freelancer/freelancerApi";

import {
  SUPERVISOR_EVALUATION_PICK_FORM_PAGE,
  SUPERVISOR_STORE_EVALUATION_PAGE,
  SUPERVISOR_STORE_EVALUATION_START_PAGE,
} from "../routes";
import { ESTOQUE_BALANCO_LOJAS_PAGE, ESTOQUE_PRODUTOS_PAGE } from "../../estoque/routes";
import { UPDATE_PRESENCE_TABLE_PAGE } from "../../update-presence/routes";
import { RGB_HOME_PAGE } from "../../rgb/routes";
import { listSubmittedEvaluationsByRegistrationApi } from "../supervisorApi";

import "./supervisor-home.css";

type Shortcut = {
  key: string;
  title: string;
  description: string;
  icon: EcoIconName;
  /** Cor do ícone (fundo claro + traço), como os cartões do desenho. */
  tone: "green" | "blue" | "amber" | "gray";
  onClick: () => void;
};

/** "Bom dia" / "Boa tarde" / "Boa noite" pelo relógio do aparelho, como a Home do admin. */
const saudacao = (agora: Date) => {
  const hora = agora.getHours();
  if (hora < 5 || hora >= 18) return "Boa noite";
  if (hora < 12) return "Bom dia";
  return "Boa tarde";
};

const dataPorExtenso = (agora: Date) =>
  new Intl.DateTimeFormat("pt-BR", { weekday: "long", day: "numeric", month: "long" }).format(agora);

/** "salva há 5 min" / "salva ontem às 14:32", para a auditoria em andamento. */
const salvaHa = (iso: string, agora: Date) => {
  const quando = new Date(iso);
  const minutos = Math.floor((agora.getTime() - quando.getTime()) / 60000);
  if (minutos < 1) return "salva agora";
  if (minutos < 60) return `salva há ${minutos} min`;
  const hora = quando.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
  if (quando.toDateString() === agora.toDateString()) return `salva hoje às ${hora}`;
  return `salva em ${quando.toLocaleDateString("pt-BR", { day: "2-digit", month: "2-digit" })} às ${hora}`;
};

/** Hoje em São Paulo, `AAAA-MM-DD`. */
const hojeYmd = () => new Date().toLocaleDateString("en-CA", { timeZone: "America/Sao_Paulo" });

const diaCurto = (ymd: string) => {
  const [, m, d] = ymd.slice(0, 10).split("-");
  return `${d}/${m}`;
};

/**
 * Início do supervisor (redesign de 15/10/2026). A Topbar vem do `StaffLayout`;
 * os atalhos são os mesmos de antes, agrupados em Avaliar, Rotina da loja e
 * Consultar. Supervisor de campo vê os 7; supervisor, 3.
 */
const SupervisorHomePage: React.FC = () => {
  const navigate = useNavigate();

  const token = useSelector((state: RootState) => state.auth.accessToken);
  const userType = useSelector((state: RootState) => state.auth.userType);
  const isField = userType === "SUPERVISOR_DE_CAMPO";
  const { firstName } = useStaffProfile();
  const agora = new Date();

  const [isEvalModalOpen, setIsEvalModalOpen] = useState(false);
  const [isStoreEvalModalOpen, setIsStoreEvalModalOpen] = useState(false);
  const [isViewEvaluationsModalOpen, setIsViewEvaluationsModalOpen] = useState(false);
  const [isViewStoreEvaluationsModalOpen, setIsViewStoreEvaluationsModalOpen] = useState(false);
  const [openAudits, setOpenAudits] = useState<MyOpenStoreEvaluation[]>([]);
  const [coverage, setCoverage] = useState<MyCoverage | null>(null);

  // Auditorias com rascunho: só quem avalia loja. Falha aqui não atrapalha a home.
  useEffect(() => {
    if (!token || !isField) return;
    listMyOpenStoreEvaluationsApi({ token })
      .then(setOpenAudits)
      .catch(() => setOpenAudits([]));
    // Lojas vinculadas e quais já tiveram visita nesta semana (segunda a domingo).
    getMyCoverageApi()
      .then(setCoverage)
      .catch(() => setCoverage(null));
  }, [token, isField]);

  const ensureSession = useCallback(() => {
    if (!token) {
      toast.error("Sessão inválida. Faça login novamente.");
      navigate(LOGINPAGE(), { replace: true });
      return false;
    }
    return true;
  }, [token, navigate]);

  const open = useCallback(
    (action: () => void) => () => {
      if (ensureSession()) action();
    },
    [ensureSession],
  );

  const fetchStoresPage = useCallback(async (args: { token: string; page: number; limit: number }) => {
    return await getMyStoresPaginatedApi(args.token, args.page, args.limit);
  }, []);

  const fetchFreelancersForEvaluation = useCallback(
    async (args: { token: string; storeId: string; dayYmd: string; slotId: string }) => {
      const data: PaginatedSlotFreelancersResponse = await getFreelancersBySlotApi(args.token, args.slotId, {
        page: 1,
        limit: 200,
      });

      const items: SlotFreelancerItem[] = data.items ?? [];

      return items.map((it) => ({
        registrationId: (it as any).registrationId,
        freelancerId: it.freelancerId,
        fullName: it.nomeCompleto,
        cpf: it.cpf,
        presenceStatus: it.status,
      })) as EvaluationFreelancerCandidate[];
    },
    [],
  );

  const buildEvaluationPath = useCallback((args: { registrationId: string }) => {
    return SUPERVISOR_EVALUATION_PICK_FORM_PAGE(args.registrationId);
  }, []);

  const buildStoreEvaluationPath = useCallback((args: { storeId: string; dayYmd: string }) => {
    const qs = new URLSearchParams();
    qs.set("storeId", args.storeId);
    qs.set("day", args.dayYmd);
    return `${SUPERVISOR_STORE_EVALUATION_START_PAGE()}?${qs.toString()}`;
  }, []);

  const fetchSubmittedEvaluationsByRegistration = useCallback(
    async (args: { token: string; registrationId: string }) => {
      return await listSubmittedEvaluationsByRegistrationApi(args);
    },
    [],
  );

  const fetchStoreEvaluations = useCallback(
    async (args: { token: string; storeId: string; referenceDate: string; includeAnswers: boolean }) => {
      return await listStoreEvaluationsApi(args);
    },
    [],
  );

  const rotina: Shortcut[] = [
    {
      key: "presenca",
      title: "Presença dos freelancers",
      description: "Confirme quem veio, quem faltou e quem cancelou.",
      icon: "userCheck",
      tone: "blue",
      onClick: open(() => navigate(UPDATE_PRESENCE_TABLE_PAGE())),
    },
    // Os dois papéis apontam o vasilhame (BUG-012: antes o supervisor só chegava digitando o endereço).
    {
      key: "rgb",
      title: "Vasilhame RGB",
      description: "Aponte refugo, recebimento e devolução ao CDD.",
      icon: "bottle",
      tone: "green",
      onClick: open(() => navigate(RGB_HOME_PAGE())),
    },
    ...(isField
      ? ([
          {
            key: "balanco",
            title: "Balanço de massa",
            description: "Abra ou continue a contagem do dia da loja.",
            icon: "box",
            tone: "amber",
            onClick: open(() => navigate(ESTOQUE_BALANCO_LOJAS_PAGE())),
          },
          {
            key: "produtos",
            title: "Produtos e De/Para",
            description: "EAN da nota, código das vendas e packs.",
            icon: "list",
            tone: "gray",
            onClick: open(() => navigate(ESTOQUE_PRODUTOS_PAGE())),
          },
        ] as Shortcut[])
      : []),
  ];

  const consultar: Shortcut[] = [
    ...(isField
      ? ([
          {
            key: "verLojas",
            title: "Avaliações de loja",
            description: "Nota, respostas e fotos de cada auditoria.",
            icon: "eye",
            tone: "green",
            onClick: open(() => setIsViewStoreEvaluationsModalOpen(true)),
          },
        ] as Shortcut[])
      : []),
    {
      key: "verFreelancers",
      title: "Avaliações de freelancer",
      description: "Respostas por loja, horário e freelancer.",
      icon: "eye",
      tone: "green",
      onClick: open(() => setIsViewEvaluationsModalOpen(true)),
    },
  ];

  return (
    <div className="supHome">
      <main className="supHome__main">
        <header className="supHome__header">
          <span className="supHome__date">{dataPorExtenso(agora)}</span>
          <h1 className="supHome__title">
            {saudacao(agora)}
            {firstName ? `, ${firstName}` : ""}
          </h1>
          <p className="supHome__subtitle">O que você vai fazer em campo agora?</p>
        </header>

        {/* Lembrete das lojas que faltam na semana (sexta e domingo), em destaque. */}
        <HomeNoticeBanner />

        {openAudits.length ? (
          <section className="supHome__section" aria-labelledby="sup-andamento">
            <h2 id="sup-andamento" className="supHome__sectionTitle">
              Auditorias em andamento
            </h2>
            <div className="supHome__list">
              {openAudits.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  className="supHome__row"
                  onClick={open(() =>
                    navigate(SUPERVISOR_STORE_EVALUATION_START_PAGE(), { state: { evaluationId: a.id } }),
                  )}
                >
                  <EcoIcon name="store" size={22} className="supHome__rowIcon" />
                  <span className="supHome__cardText">
                    <span className="supHome__rowTitle">
                      {a.store.nomeFantasia} · {diaCurto(a.referenceDate)}
                    </span>
                    <span className="supHome__cardDesc">
                      {a.form.title} · {a._count.answers}{" "}
                      {a._count.answers === 1 ? "respondida" : "respondidas"} · {salvaHa(a.updatedAt, agora)}
                    </span>
                  </span>
                  <EcoIcon name="chevronRight" size={20} className="supHome__chevronMuted" />
                </button>
              ))}
            </div>
          </section>
        ) : null}

        {isField && coverage ? (
          <section className="supHome__section" aria-labelledby="sup-semana">
            <h2 id="sup-semana" className="supHome__sectionTitle">
              Minhas lojas da semana
              {coverage.total ? (
                <span className="supHome__sectionCount">
                  {coverage.done} de {coverage.total} visitadas · {diaCurto(coverage.weekStart)} a {diaCurto(coverage.weekEnd)}
                </span>
              ) : null}
            </h2>
            {coverage.total ? (
              <div className="supHome__list">
                {coverage.stores.map((store) => (
                  <button
                    key={store.id}
                    type="button"
                    className="supHome__row"
                    onClick={open(() =>
                      store.done && store.evaluationId
                        ? navigate(SUPERVISOR_STORE_EVALUATION_PAGE(store.evaluationId))
                        : navigate(buildStoreEvaluationPath({ storeId: store.id, dayYmd: hojeYmd() })),
                    )}
                  >
                    <EcoIcon
                      name={store.done ? "check" : "store"}
                      size={22}
                      className={`supHome__rowIcon${store.done ? " supHome__rowIcon--done" : ""}`}
                    />
                    <span className="supHome__cardText">
                      <span className="supHome__rowTitle">{store.nomeFantasia}</span>
                      <span className="supHome__cardDesc">
                        {store.done && store.referenceDate ? `Visitada em ${diaCurto(store.referenceDate)}` : "Sem visita nesta semana · toque para avaliar"}
                      </span>
                    </span>
                    <EcoIcon name="chevronRight" size={20} className="supHome__chevronMuted" />
                  </button>
                ))}
              </div>
            ) : (
              <p className="supHome__empty">Nenhuma loja vinculada ao seu usuário. Peça ao administrador para vincular as suas lojas.</p>
            )}
          </section>
        ) : null}

        <section className="supHome__section" aria-labelledby="sup-avaliar">
          <h2 id="sup-avaliar" className="supHome__sectionTitle">
            Avaliar
          </h2>
          <div className={`supHome__primary${isField ? "" : " supHome__primary--single"}`}>
            {isField ? (
              <button
                type="button"
                className="supHome__hero supHome__hero--solid"
                onClick={open(() => setIsStoreEvalModalOpen(true))}
              >
                <span className="supHome__heroIcon" aria-hidden="true">
                  <EcoIcon name="store" size={26} strokeWidth={1.8} />
                </span>
                <span className="supHome__heroText">
                  <span className="supHome__heroTitle">Avaliar loja</span>
                  <span className="supHome__heroDesc">Auditoria com foto de entrada, perguntas e foto de saída.</span>
                </span>
                <EcoIcon name="chevronRight" size={22} className="supHome__chevron" />
              </button>
            ) : null}
            <button type="button" className="supHome__hero" onClick={open(() => setIsEvalModalOpen(true))}>
              <span className="supHome__heroIcon" aria-hidden="true">
                <EcoIcon name="star" size={26} strokeWidth={1.8} />
              </span>
              <span className="supHome__heroText">
                <span className="supHome__heroTitle">Avaliar freelancer</span>
                <span className="supHome__heroDesc">Escolha a loja, o horário e quem trabalhou.</span>
              </span>
              <EcoIcon name="chevronRight" size={22} className="supHome__chevron" />
            </button>
          </div>
        </section>

        <section className="supHome__section" aria-labelledby="sup-rotina">
          <h2 id="sup-rotina" className="supHome__sectionTitle">
            Rotina da loja
          </h2>
          <div className="supHome__grid">
            {rotina.map((s) => (
              <button key={s.key} type="button" className="supHome__card" onClick={s.onClick}>
                <span className={`supHome__cardIcon supHome__cardIcon--${s.tone}`} aria-hidden="true">
                  <EcoIcon name={s.icon} size={24} strokeWidth={1.8} />
                </span>
                <span className="supHome__cardText">
                  <span className="supHome__cardTitle">{s.title}</span>
                  <span className="supHome__cardDesc">{s.description}</span>
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="supHome__section" aria-labelledby="sup-consultar">
          <h2 id="sup-consultar" className="supHome__sectionTitle">
            Consultar o que já foi enviado
          </h2>
          <div className="supHome__list">
            {consultar.map((s) => (
              <button key={s.key} type="button" className="supHome__row" onClick={s.onClick}>
                <EcoIcon name={s.icon} size={22} className="supHome__rowIcon" />
                <span className="supHome__cardText">
                  <span className="supHome__rowTitle">{s.title}</span>
                  <span className="supHome__cardDesc">{s.description}</span>
                </span>
                <EcoIcon name="chevronRight" size={20} className="supHome__chevronMuted" />
              </button>
            ))}
          </div>
        </section>
      </main>

      <SelectFreelancersForEvaluationModal
        isOpen={isEvalModalOpen}
        onClose={() => setIsEvalModalOpen(false)}
        token={token ?? ""}
        fetchFreelancersForEvaluation={fetchFreelancersForEvaluation}
        buildEvaluationPath={buildEvaluationPath}
      />

      <SelectStoresForEvaluationModal
        isOpen={isStoreEvalModalOpen}
        onClose={() => setIsStoreEvalModalOpen(false)}
        token={token ?? ""}
        buildEvaluationPath={buildStoreEvaluationPath}
      />

      <ViewSubmittedEvaluationsModal
        isOpen={isViewEvaluationsModalOpen}
        onClose={() => setIsViewEvaluationsModalOpen(false)}
        token={token ?? ""}
        fetchStoresPage={fetchStoresPage}
        fetchFreelancersForEvaluation={fetchFreelancersForEvaluation}
        fetchSubmittedEvaluationsByRegistration={fetchSubmittedEvaluationsByRegistration}
      />

      <ViewStoreEvaluationsModal
        isOpen={isViewStoreEvaluationsModalOpen}
        onClose={() => setIsViewStoreEvaluationsModalOpen(false)}
        token={token ?? ""}
        fetchStoresPage={fetchStoresPage}
        fetchStoreEvaluations={fetchStoreEvaluations}
      />
    </div>
  );
};

export default SupervisorHomePage;
